import React, { useState } from 'react';
import { EventData } from '@spotify-internal/event-sender';
import { EventSenderClient } from 'services/sender';
import { Country, HomeId } from 'types';
import { UserType } from 'lib/tracking';
import { PlanType } from 'lib/plans';
import { createDNFPageView } from '@spotify-internal/event-definitions/es5/events/createDNFPageView';
import Cookie from 'js-cookie';
import { Locale } from 'lib/marketLocales';
import { CommonTrackingParams, PageType } from 'lib/trackingEvents';
import { useTranslation } from 'data/translation/useTranslation';
import { usePlan } from 'data/plan/usePlan';

type EventProperties = Record<string, string> | null;

const defaultContext: TrackingContextType = {
  sendEvent: () => {},
  pageView: () => {},
  trackEvent: () => {},
  setCountry: () => {},
  setHomeId: () => {},
  setUserType: () => {},
};

export type EventDataFactory = (
  commonParams: CommonTrackingParams,
) => EventData;

export type TrackingContextType = {
  sendEvent: (eventData: EventData) => void;
  pageView: (pageType: PageType, properties: EventProperties) => void;
  trackEvent: (factory: EventDataFactory) => void;
  setCountry: (country: Country) => void;
  setHomeId: (homeId: HomeId) => void;
  setUserType: (userType: UserType) => void;
};

const sender =
  typeof window !== 'undefined' ? EventSenderClient.getInstance() : null;

export function getTrackingContext({
  country,
  userType,
  planType,
  homeId,
  locale,
  setCountry,
  setHomeId,
  setUserType,
}: {
  country: Country;
  userType: UserType;
  planType: PlanType;
  homeId: HomeId;
  locale: Locale;
  setCountry: (country: Country) => void;
  setHomeId: (homeId: HomeId) => void;
  setUserType: (userType: UserType) => void;
}): TrackingContextType {
  function sendEvent(eventData: EventData) {
    if (!sender) {
      return Promise.resolve();
    }
    if (typeof window.__DNF_EVENT_LOGGER__ === 'object') {
      try {
        window.__DNF_EVENT_LOGGER__.push(eventData);
      } catch (e) {
        /**/
      }
    }
    return sender.send(eventData, {
      flush: true,
    }) as Promise<void>;
  }
  function pageView(pageType: PageType, properties: EventProperties = null) {
    const event = createDNFPageView({
      user_type: userType,
      plan_type: planType,
      country: country,
      home_id: homeId,
      sp_t: Cookie.get('sp_t'),
      market: locale,
      page_type: pageType,
      ...(properties && { properties }),
    });
    return sendEvent(event);
  }

  function trackEvent(
    factory: (commonParams: CommonTrackingParams) => EventData,
  ) {
    return sendEvent(
      factory({
        user_type: userType,
        plan_type: planType,
        country: country,
        home_id: homeId,
        sp_t: Cookie.get('sp_t'),
        market: locale,
      }),
    );
  }
  return {
    sendEvent,
    trackEvent,
    pageView,
    setCountry,
    setHomeId,
    setUserType,
  };
}

export const TrackingContext =
  React.createContext<TrackingContextType>(defaultContext);

type TrackingProviderProps = {
  children: React.ReactNode;
};

export const TrackingProvider = (props: TrackingProviderProps) => {
  const { children } = props;

  const { locale } = useTranslation();
  const { planType } = usePlan();

  const [homeId, setHomeId] = useState<HomeId>('');
  const [country, setCountry] = useState<Country>('');
  const [userType, setUserType] = useState<UserType>('');

  return (
    <TrackingContext.Provider
      value={getTrackingContext({
        country,
        userType,
        planType,
        homeId,
        locale,
        setCountry,
        setHomeId,
        setUserType,
      })}
    >
      {children}
    </TrackingContext.Provider>
  );
};
